import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";
import {
  ArrowUp,
  Book,
  CloudHail,
  Droplet,
  FileText,
  Layers,
} from "lucide-react";
/**
 * Wissen hero module — 1:1 port of the prototype's `isWissen` blocks
 * (Hausverwaltung Hero.dc.html: sidebar ~L496-518, wiki article ~L1284-1320).
 *
 * Normal (chromed) module: a 220px sidebar tree + flex content rendering the
 * "Mietbescheinigung ausstellen" wiki article. All copy is verbatim German.
 */
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H, MODULE_HEIGHT, SIDEBAR_WIDTH } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

/* ── Sidebar tree ────────────────────────────────────────────────────────── */

type LeafIcon = "file-text" | "droplet" | "cloud-hail";

interface SectionHeader {
  readonly kind: "header";
  readonly label: string;
  /** Extra top padding for non-first headers (prototype: 11px vs 7px). */
  readonly spaced?: boolean;
}
interface LeafRow {
  readonly kind: "leaf";
  readonly label: string;
  readonly icon: LeafIcon;
  /** Left indent in px (16 = first level, 30 = nested). */
  readonly indent: number;
  readonly active?: boolean;
  /** Deeper-nested rows render with lighter ink (prototype 0.66 vs 0.72). */
  readonly faint?: boolean;
}
type TreeRow = SectionHeader | LeafRow;

function tree(t: Translate): readonly TreeRow[] {
  return [
    { kind: "header", label: t("allgemein") },
    {
      kind: "leaf",
      label: t("willkommenWissen"),
      icon: "file-text",
      indent: 16,
    },
    {
      kind: "leaf",
      label: t("glossarAbkuerzungen"),
      icon: "file-text",
      indent: 16,
    },
    { kind: "header", label: t("prozesse"), spaced: true },
    {
      kind: "leaf",
      label: t("schadensmeldungBearbeiten"),
      icon: "file-text",
      indent: 16,
    },
    {
      kind: "leaf",
      label: t("wasserschaden"),
      icon: "droplet",
      indent: 30,
      faint: true,
    },
    {
      kind: "leaf",
      label: t("sturmHagelschaden"),
      icon: "cloud-hail",
      indent: 30,
      faint: true,
    },
    {
      kind: "leaf",
      label: t("mietbescheinigungAusstellen"),
      icon: "file-text",
      indent: 16,
      active: true,
    },
    {
      kind: "leaf",
      label: t("handwerkerauftragBeauftragen"),
      icon: "file-text",
      indent: 16,
    },
    {
      kind: "leaf",
      label: t("schluesselNachbestellen"),
      icon: "file-text",
      indent: 16,
    },
    { kind: "header", label: t("eigentuemerversammlung"), spaced: true },
    {
      kind: "leaf",
      label: t("einladungFristen"),
      icon: "file-text",
      indent: 16,
    },
    {
      kind: "leaf",
      label: t("beschluesseProtokollieren"),
      icon: "file-text",
      indent: 16,
    },
  ];
}

function LeafGlyph({ icon }: { readonly icon: LeafIcon }): ReactNode {
  if (icon === "droplet") {
    return <Droplet width={13} height={13} strokeWidth={1.8} />;
  }
  if (icon === "cloud-hail") {
    return <CloudHail width={13} height={13} strokeWidth={1.8} />;
  }
  return <FileText width={14} height={14} strokeWidth={1.8} />;
}

function SidebarTree(): ReactNode {
  const t = useTranslations("marketing.moduleWissen");
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {tree(t).map((row, i) => {
        if (row.kind === "header") {
          return (
            <div
              key={`h-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: row.spaced ? "11px 8px 5px" : "7px 8px 5px",
                fontSize: 13.5,
                fontWeight: 700,
                color: H.ink,
              }}
            >
              <span style={{ color: H.inkFaint, display: "inline-flex" }}>
                <Book width={15} height={15} strokeWidth={1.8} />
              </span>
              {row.label}
            </div>
          );
        }
        const activeStyle: CSSProperties = row.active
          ? { fontWeight: 600, color: H.accent, background: H.accentSoft }
          : {};
        return (
          <div
            key={`l-${i}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: `5px 8px 5px ${row.indent}px`,
              borderRadius: 7,
              fontSize: 13,
              color: row.faint ? ld("rgba(11,15,25,0.66)", "rgba(250,250,250,0.66)") : ld("rgba(11,15,25,0.72)", "rgba(250,250,250,0.72)"),
              ...activeStyle,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                color: row.active ? H.accent : ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
              }}
            >
              <LeafGlyph icon={row.icon} />
            </span>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Wiki article ────────────────────────────────────────────────────────── */

/* ── Wiki article ────────────────────────────────────────────────────────── */
function ablauf(t: Translate): readonly string[] {
  return [
    t("anfrageVorgangOeffnenMieter"),
    t("mietverhaeltnisEinheitPruefenAdresse"),
    t("vorlageMietbescheinigungWaehlenFelder"),
    t("dokumentPruefenPdfErzeugen"),
    t("bescheinigungMieterSendenLiegenschaft"),
  ];
}

function pruefpunkte(t: Translate): readonly string[] {
  return [
    t("stimmenNameAnschriftStammdaten"),
    t("aktuelleKaltmieteHinterlegt"),
    t("liegtAnfragenDritterVollmacht"),
  ];
}

const H2: CSSProperties = {
  fontSize: 19,
  fontWeight: 700,
  color: H.ink,
};

function Article(): ReactNode {
  const t = useTranslations("marketing.moduleWissen");
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        padding: "22px 2px 0",
      }}
    >
      <h1
        style={{
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: H.ink,
          margin: 0,
        }}
      >
        {t("mietbescheinigungAusstellen")}
      </h1>
      <p
        style={{
          fontSize: 15,
          color: H.inkMute,
          lineHeight: 1.6,
          margin: "14px 0 0",
        }}
      >
        {t("dieseSeiteBeschreibtMietbescheinigung")}
      </p>
      <h2 style={{ ...H2, margin: "24px 0 0" }}>{t("zweck")}</h2>
      <p
        style={{
          fontSize: 14.5,
          color: H.inkSoft,
          lineHeight: 1.6,
          margin: "9px 0 0",
        }}
      >
        {t("mietbescheinigungBestaetigtBestehendeMietverhaeltnis")}
      </p>
      <h2 style={{ ...H2, margin: "22px 0 0" }}>{t("ablauf")}</h2>
      <ol
        style={{
          margin: "11px 0 0",
          paddingLeft: 20,
          fontSize: 14.5,
          color: H.inkSoft,
          lineHeight: 1.9,
        }}
      >
        {ablauf(t).map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <h2 style={{ ...H2, margin: "22px 0 0" }}>{t("wichtigePruefpunkte")}</h2>
      <ul
        style={{
          margin: "11px 0 0",
          paddingLeft: 20,
          fontSize: 14.5,
          color: H.inkSoft,
          lineHeight: 1.9,
        }}
      >
        {pruefpunkte(t).map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </div>
  );
}

/* ── Module ──────────────────────────────────────────────────────────────── */

export function ModuleWissen(_props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleWissen");
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT, fontFamily: H.sans }}>
      {/* Sidebar */}
      <div
        style={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          borderRight: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
          background: ld("#FBFBFC", "#1d1f23"),
          padding: "16px 12px",
        }}
      >
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0 6px 12px",
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 700 }}>{t("wissen")}</span>
          </div>
          <SidebarTree />
          <div
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 11px",
              border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
              borderRadius: 9,
              fontSize: 13,
              color: H.inkMute,
              cursor: "pointer",
            }}
          >
            <Layers width={15} height={15} strokeWidth={1.8} />
            {t("neuerBereich")}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, padding: "20px 22px" }}>
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 14,
              borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13.5,
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: H.inkFaint,
                }}
              >
                <Book width={15} height={15} strokeWidth={1.8} />
                {t("prozesse")}
              </span>
              <span style={{ color: ld("rgba(11,15,25,0.3)", "rgba(250,250,250,0.3)") }}>/</span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: H.ink,
                  fontWeight: 600,
                }}
              >
                <FileText width={15} height={15} strokeWidth={1.8} />
                {t("mietbescheinigungAusstellen")}
              </span>
            </div>
          </div>

          <Article />

          <div style={{ paddingTop: 10 }}>
            <div
              style={{
                border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
                borderRadius: 9,
                padding: "7px 9px 7px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 12.5, color: ld("rgba(11,15,25,0.38)", "rgba(250,250,250,0.38)") }}>
                {t("wissenHinzufuegen")}
              </span>
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 7,
                  background: ld("#F1F2F4", "#2b2e32"),
                  color: H.inkFaint,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <ArrowUp width={14} height={14} strokeWidth={2} />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
